/**
 * MonitorAiPanel — AI estate summary for the monitor page.
 *
 * Replaces MonitorChatBot. Auto-triggers a summary when monitor data is
 * ready and provides a "Continue the conversation" button that opens the
 * AI agent drawer pre-seeded with monitor context.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bot, MessageSquare, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardSub, CardTitle } from "@/components/ui/card";
import { useAiExplain, type AiExplanation } from "@/stores/ai-explain";
import { useAiAgent } from "@/stores/ai-agent";
import { cn } from "@/lib/cn";
import type {
  Alert,
  NetworkEventRow,
  NetworkLicenseRollup,
  SiteHealth,
} from "@/api/schemas/monitor";

/* ---------- types ---------- */

export interface MonitorAiPanelProps {
  alerts: Alert[];
  sites: SiteHealth[];
  alertsHistory: NetworkEventRow[];
  networkEvents: NetworkEventRow[];
  licenses: NetworkLicenseRollup[];
  criticals: number;
  warnings: number;
  sitesAtRisk: number;
  uplinkSum: number;
  timeframeLabel: string;
  allDataReady: boolean;
  className?: string;
}

/* ---------- pure data helpers ---------- */

function computeHotspotSites(sites: SiteHealth[]) {
  return sites
    .filter((s) => s.alertsActive > 0 || s.devicesOnline < s.devicesTotal)
    .sort((a, b) => {
      if (b.alertsActive !== a.alertsActive)
        return b.alertsActive - a.alertsActive;
      const avA = a.devicesTotal ? a.devicesOnline / a.devicesTotal : 1;
      const avB = b.devicesTotal ? b.devicesOnline / b.devicesTotal : 1;
      return avA - avB;
    })
    .slice(0, 8);
}

function computeAlertCounts(alerts: Alert[], openAlerts: Alert[]) {
  const bySeverity: Record<"critical" | "warning" | "info", number> = {
    critical: 0,
    warning: 0,
    info: 0,
  };
  const byVendor: Record<string, number> = {};
  for (const alert of openAlerts) {
    bySeverity[alert.severity] += 1;
    byVendor[alert.vendor] = (byVendor[alert.vendor] ?? 0) + 1;
  }
  return {
    open: openAlerts.length,
    acked: Math.max(0, alerts.length - openAlerts.length),
    total: alerts.length,
    bySeverity,
    byVendor: Object.entries(byVendor)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([vendor, count]) => ({ vendor, count })),
  };
}

function computeSiteStats(sites: SiteHealth[]) {
  const buckets = { healthy: 0, warning: 0, critical: 0 };
  const rows = sites.map((site) => {
    const availabilityPct =
      site.devicesTotal > 0
        ? Math.round((site.devicesOnline / site.devicesTotal) * 100)
        : 100;
    const riskScore = site.alertsActive * 100 + (100 - availabilityPct);
    if (site.alertsActive >= 5 || availabilityPct < 80) {
      buckets.critical += 1;
    } else if (site.alertsActive > 0 || availabilityPct < 95) {
      buckets.warning += 1;
    } else {
      buckets.healthy += 1;
    }
    return {
      name: site.name,
      hotelName: site.hotelName ?? site.name,
      region: site.region,
      alertsActive: site.alertsActive,
      devicesOnline: site.devicesOnline,
      devicesTotal: site.devicesTotal,
      availabilityPct,
      riskScore,
      primaryConcern:
        site.alertsActive >= 5
          ? "high alert volume"
          : availabilityPct < 80
            ? "low device availability"
            : site.alertsActive > 0
              ? "active alerts"
              : "availability drift",
    };
  });
  const topProblemSites = rows
    .filter((row) => row.alertsActive > 0 || row.availabilityPct < 100)
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 10);
  return { buckets, topProblemSites };
}

function computeEventStats(
  alertsHistory: NetworkEventRow[],
  networkEvents: NetworkEventRow[],
) {
  const typeCounts: Record<string, number> = {};
  const networkCounts: Record<string, number> = {};
  for (const row of alertsHistory) {
    typeCounts[row.alertType] = (typeCounts[row.alertType] ?? 0) + 1;
    networkCounts[row.networkName] = (networkCounts[row.networkName] ?? 0) + 1;
  }
  const externalTypeCounts: Record<string, number> = {};
  for (const row of networkEvents) {
    externalTypeCounts[row.alertType] =
      (externalTypeCounts[row.alertType] ?? 0) + 1;
  }
  return {
    historyCount: alertsHistory.length,
    networkEventCount: networkEvents.length,
    topHistoryTypes: Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([type, count]) => ({ type, count })),
    topHistorySites: Object.entries(networkCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([site, count]) => ({ site, count })),
    topNetworkEventTypes: Object.entries(externalTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([type, count]) => ({ type, count })),
  };
}

function computeLicenseStats(licenses: NetworkLicenseRollup[]) {
  const totals = {
    rollupCount: licenses.length,
    totalLicenses: 0,
    totalDevices: 0,
    totalUnlicensed: 0,
    expired: 0,
    expiring: 0,
  };
  for (const row of licenses) {
    totals.totalLicenses += row.licenseCount;
    totals.totalDevices += row.devicesTotal;
    totals.totalUnlicensed += row.unlicensedCount;
    totals.expired += row.expiredCount;
    totals.expiring += row.aboutToExpireCount;
  }
  const atRiskSites = licenses
    .filter((row) => row.expiredCount > 0 || row.unlicensedCount > 0)
    .sort(
      (a, b) =>
        b.expiredCount + b.unlicensedCount - (a.expiredCount + a.unlicensedCount),
    )
    .slice(0, 8)
    .map((row) => ({
      networkName: row.networkName,
      networkSlug: row.networkSlug,
      expiredCount: row.expiredCount,
      unlicensedCount: row.unlicensedCount,
    }));
  return { ...totals, atRiskSites };
}

/* ---------- component ---------- */

export function MonitorAiPanel({
  alerts,
  sites,
  alertsHistory,
  networkEvents,
  licenses,
  criticals,
  warnings,
  sitesAtRisk,
  uplinkSum,
  timeframeLabel,
  allDataReady,
  className,
}: MonitorAiPanelProps) {
  const aiLoad = useAiExplain((s) => s.load);
  const setDrawerOpen = useAiAgent((s) => s.setOpen);
  const sendToAgent = useAiAgent((s) => s.send);

  const [summarizing, setSummarizing] = useState(false);
  const [summaryState, setSummaryState] = useState<{
    key: string;
    data: AiExplanation | null;
    error: string | null;
  }>({ key: "", data: null, error: null });
  // Tracks which contextKey was last auto-triggered so we don't fire twice for
  // the same snapshot. Separate from lastContextKeyRef (which drives inline reset).
  const autoSummarizedKeyRef = useRef<string>("");
  // Ref mirror of `summarizing` state — lets the effect guard against double-
  // trigger without listing state in the dependency array.
  const summarizingRef = useRef(false);

  const hotspotSites = useMemo(() => computeHotspotSites(sites), [sites]);
  const openAlerts = useMemo(() => alerts.filter((a) => !a.ackedAt), [alerts]);
  const alertCounts = useMemo(
    () => computeAlertCounts(alerts, openAlerts),
    [alerts, openAlerts],
  );
  const siteStats = useMemo(() => computeSiteStats(sites), [sites]);
  const eventStats = useMemo(
    () => computeEventStats(alertsHistory, networkEvents),
    [alertsHistory, networkEvents],
  );
  const licenseStats = useMemo(() => computeLicenseStats(licenses), [licenses]);

  const context = useMemo(() => {
    if (sites.length === 0) return null;
    return {
      estate: {
        siteCount: sites.length,
        timeframeLabel,
        openAlerts: alertCounts.open,
        criticals,
        warnings,
        sitesAtRisk,
        aggregateUplinkMbps: Math.round(uplinkSum * 100) / 100,
      },
      kpis: { criticals, warnings, sitesAtRisk, aggregateUplinkMbps: Math.round(uplinkSum * 100) / 100 },
      alerts: alertCounts,
      sites: { distribution: siteStats.buckets },
      events: eventStats,
      licenses: licenseStats,
      hotspots: hotspotSites.map((s) => ({
        name: s.name,
        hotelName: s.hotelName ?? s.name,
        region: s.region,
        alertsActive: s.alertsActive,
        devicesOnline: s.devicesOnline,
        devicesTotal: s.devicesTotal,
        availabilityPct:
          s.devicesTotal > 0
            ? Math.round((s.devicesOnline / s.devicesTotal) * 100)
            : 100,
      })),
      focusSites: siteStats.topProblemSites.slice(0, 5).map((site) => ({
        name: site.name,
        hotelName: site.hotelName,
        region: site.region,
        alertsActive: site.alertsActive,
        availabilityPct: site.availabilityPct,
        primaryConcern: site.primaryConcern,
      })),
      topProblemSites: siteStats.topProblemSites,
    };
  }, [
    sites, timeframeLabel, alertCounts, criticals, warnings,
    sitesAtRisk, uplinkSum, siteStats, eventStats, licenseStats, hotspotSites,
  ]);

  const contextKey = useMemo(() => JSON.stringify(context ?? {}), [context]);

  // Derive current summary data/error from the keyed atom — stale on context change.
  const summaryData = summaryState.key === contextKey ? summaryState.data : null;
  const summaryError = summaryState.key === contextKey ? summaryState.error : null;

  const ready = Boolean(context) && allDataReady;

  const summarize = useCallback(async (force = false) => {
    if (!context || !allDataReady) return;
    const key = contextKey;
    summarizingRef.current = true;
    setSummarizing(true);
    setSummaryState((prev) => ({ ...prev, key, error: null }));
    try {
      const result = await aiLoad(
        "monitor-dashboard-summary",
        context as Record<string, unknown>,
        force ? { force: true } : undefined,
      );
      if (result) {
        setSummaryState({ key, data: result, error: null });
      } else {
        setSummaryState({ key, data: null, error: "AI estate summary is temporarily unavailable" });
      }
    } catch (e) {
      setSummaryState({
        key,
        data: null,
        error: e instanceof Error ? e.message : "Failed to generate summary",
      });
    } finally {
      summarizingRef.current = false;
      setSummarizing(false);
    }
  }, [aiLoad, allDataReady, context, contextKey]);

  // Auto-trigger once per context snapshot when all data is ready.
  // Refs guard against double-trigger; no state in deps to avoid extra renders.
  useEffect(() => {
    if (!ready || summarizingRef.current || autoSummarizedKeyRef.current === contextKey) return;
    autoSummarizedKeyRef.current = contextKey;
    void summarize(false);
  }, [ready, contextKey, summarize]);

  function handleContinue() {
    if (!context) return;
    const topSites = hotspotSites
      .slice(0, 5)
      .map(
        (s) =>
          `${s.hotelName ?? s.name} (${s.alertsActive} alerts, ${s.devicesOnline}/${s.devicesTotal} online)`,
      )
      .join("; ");
    const prompt = `I'm reviewing the monitor dashboard. Estate: ${context.estate.siteCount} sites, ${context.estate.openAlerts} open alerts (${criticals} critical, ${warnings} warning), ${sitesAtRisk} sites at risk. ${topSites ? `Top issues: ${topSites}.` : ""} ${summaryData ? `Summary: ${summaryData.summary}` : ""} Let's dig deeper into the issues.`;
    setDrawerOpen(true);
    void sendToAgent(prompt);
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Panel header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-aurora-soft">
            <Bot size={15} className="text-violet" />
          </div>
          <div>
            <CardTitle className="text-sm">AI Estate Summary</CardTitle>
            <CardSub className="text-[11px]">
              {sites.length > 0
                ? `${sites.length} sites · ${openAlerts.length} open alerts`
                : "Waiting for data…"}
            </CardSub>
          </div>
        </div>
        {summaryData && ready ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void summarize(true)}
            disabled={summarizing}
          >
            <Sparkles size={11} className="text-violet" />
            Re-summarize
          </Button>
        ) : null}
      </div>

      {/* AI summary body */}
      <div className="flex min-h-[120px] flex-1 flex-col">
        {!ready ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-center">
            <RefreshCw size={16} className="animate-spin text-violet" />
            <p className="text-sm text-fg-mute">
              Loading full monitor snapshot…
            </p>
          </div>
        ) : summarizing ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-center">
            <RefreshCw size={16} className="animate-spin text-violet" />
            <p className="text-sm text-fg-mute">Generating estate summary…</p>
          </div>
        ) : summaryError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-peach/30 bg-peach/10 px-4 py-4 text-center">
            <p className="text-sm text-peach">{summaryError}</p>
            <Button
              size="sm"
              onClick={() => void summarize(true)}
              disabled={summarizing}
            >
              Try Again
            </Button>
          </div>
        ) : summaryData ? (
          <div className="flex h-full flex-col rounded-lg border border-line bg-surface-tint/30 px-3 py-3">
            <p className="text-sm font-semibold text-fg">
              {summaryData.summary}
            </p>
            <p className="mt-1 flex-1 whitespace-pre-wrap text-sm text-fg-dim">
              {summaryData.details}
            </p>
            <p className="mt-2 font-mono text-[10.5px] text-fg-mute">
              {summaryData.model} · {summaryData.elapsedMs}ms
            </p>
          </div>
        ) : (
          <div className="flex h-full items-center rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-sm text-fg-mute">
            Summary will generate automatically when data is ready.
          </div>
        )}
      </div>

      {/* Continue conversation */}
      {(summaryData || hotspotSites.length > 0) && ready ? (
        <Button
          size="sm"
          variant="subtle"
          onClick={handleContinue}
          className="w-full"
        >
          <MessageSquare size={13} />
          Continue the conversation
        </Button>
      ) : null}
    </div>
  );
}
